import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, ScrollText, ShieldCheck, Play, Clock } from 'lucide-react';
import type { HiringRulesContentInput } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { OnboardingTerms } from '../components/hiring/OnboardingTerms';
import type { CandidateStatusResponse } from '../components/hiring/types';

export const Route = createFileRoute('/_authed/candidate/rules')({
  component: RulesLauncherPage,
});

interface RulesContentResponse {
  content: HiringRulesContentInput;
}

/**
 * Message shown in the inline "awaiting" panel and the matching race-path
 * toast. Kept as one constant so the proactive gate and the error branch
 * stay in lock-step.
 */
const AWAITING_RULES_MESSAGE =
  "The rules test hasn't been opened for you yet — we'll let you know when it's ready. There's nothing you need to do right now; the academy will email you the moment it opens.";

function RulesLauncherPage() {
  const navigate = useNavigate();
  const { toast } = useToast();

  // Reuse the exact query key + endpoint the status page uses so this is a
  // cache hit rather than a duplicate network call. The status response
  // already exposes the open-runs signal we need to gate the button on.
  const statusQuery = useQuery({
    queryKey: ['me', 'hiring', 'status'] as const,
    queryFn: ({ signal }) =>
      apiRequest<CandidateStatusResponse>('/api/me/hiring/status', { signal }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // House rules are academy-editable copy stored in KV (L-3). Fetch them
  // rather than hardcoding the wording so the owner can edit/reorder rules
  // without a deploy. This query is deliberately independent of the status
  // gate below: a slow or failed rules fetch must never block a candidate
  // who has a genuine open run — the empty/error branch degrades to neutral
  // copy while the Begin button stays driven solely by `statusQuery`.
  const rulesQuery = useQuery({
    queryKey: ['hiring', 'rules-content'] as const,
    queryFn: ({ signal }) =>
      apiRequest<RulesContentResponse>('/api/hiring/rules-content', { signal }),
    staleTime: 60_000,
  });
  const ruleSections = rulesQuery.data?.content.sections ?? [];
  const rulesIntro = rulesQuery.data?.content.intro ?? null;

  // Race fallback: HR can cancel the run after the page loaded and the
  // proactive gate passed. The mutation's error branch flips this so the
  // candidate converges on the same inline panel rather than seeing a raw
  // "not found"-style toast.
  const [awaitingFromRace, setAwaitingFromRace] = useState(false);

  // Same predicate the status page uses (scope === 'rules' && open|scheduled).
  // Only treat the candidate as "awaiting" once the status query has actually
  // resolved with no open rules run — while it's loading or has errored (e.g.
  // a transient hiring-disabled 404) we keep the Begin button enabled so a
  // status hiccup never blocks a candidate who genuinely has an open run; the
  // error branch below still catches the race.
  const hasOpenRules = !!statusQuery.data?.runs.some(
    (r) => r.scope === 'rules' && (r.status === 'open' || r.status === 'scheduled'),
  );
  const awaiting =
    awaitingFromRace || (statusQuery.isSuccess && !hasOpenRules);

  const startMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ runId: string; templateId: string }>(
        '/api/me/hiring/rules/start',
        { method: 'POST', body: {} },
      ),
    onSuccess: (data) => {
      void navigate({
        to: '/assessments/runs/$runId/start',
        params: { runId: data.runId },
      });
    },
    onError: (err: unknown) => {
      // HR hasn't opened the rules test yet (or pulled it mid-page). Surface
      // the friendly inline panel instead of a raw error toast.
      if (err instanceof ApiError && err.code === 'awaiting_rules_issue') {
        setAwaitingFromRace(true);
        void statusQuery.refetch();
        return;
      }
      // The stage gate rejected the request (e.g. the application already
      // moved past `selected`). Point the candidate at their status page.
      if (err instanceof ApiError && err.code === 'rules_not_available') {
        toast(
          "This step isn't available right now. Open your application page to see what's next.",
          { variant: 'info' },
        );
        return;
      }
      toast(
        err instanceof ApiError ? err.message : 'Could not start the rules test.',
        { variant: 'error' },
      );
    },
  });

  return (
    <ListShell
      title="Rules & regulations"
      description="The final step. Read the rules, then take the short rules test to complete onboarding."
      primaryAction={
        <Link
          to="/candidate/status"
          className="inline-flex items-center gap-1 text-sm text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
        >
          <ArrowLeft size={14} aria-hidden="true" />
          Back to my application
        </Link>
      }
    >
      <section className="mx-auto max-w-3xl space-y-6">
        <aside className="rounded-2xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] p-5 dark:from-gold-700/20">
          <div className="flex items-start gap-3">
            <span className="rounded-full bg-gold-100 p-2 text-gold-800 dark:bg-gold-700/40 dark:text-gold-50">
              <ScrollText size={20} aria-hidden="true" />
            </span>
            <div>
              <h2 className="font-display text-xl font-semibold">
                Rules &amp; Regulations of Babul Ilm Academy
              </h2>
              <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
                Please read these carefully before taking the rules test.
                You'll be asked questions based on them.
              </p>
            </div>
          </div>
        </aside>

        <OnboardingTerms heading="What you're agreeing to" />

        {rulesIntro ? (
          <p className="text-sm text-[color:var(--color-text-muted)]">
            {rulesIntro}
          </p>
        ) : null}

        {rulesQuery.isLoading ? (
          <div className="space-y-4" aria-hidden="true">
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
          </div>
        ) : ruleSections.length > 0 ? (
          <ol className="space-y-4">
            {ruleSections.map((section, idx) => (
              <li
                key={section.id}
                className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4"
              >
                <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Rule {idx + 1}
                </p>
                <h3 className="mt-0.5 font-display text-base font-semibold">
                  {section.title}
                </h3>
                <p className="mt-1 whitespace-pre-line text-sm text-[color:var(--color-text)]">
                  {section.body}
                </p>
              </li>
            ))}
          </ol>
        ) : (
          <p className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4 text-sm text-[color:var(--color-text-muted)]">
            The academy will publish the full rule list shortly. You can still
            take the rules test below.
          </p>
        )}

        {awaiting ? (
          <div
            role="status"
            className="flex flex-col gap-3 rounded-lg border border-gold-300/60 bg-gold-50 p-4 dark:bg-gold-700/20 sm:flex-row sm:items-start"
          >
            <span className="mt-0.5 shrink-0 rounded-full bg-gold-100 p-2 text-gold-800 dark:bg-gold-700/40 dark:text-gold-50">
              <Clock size={18} aria-hidden="true" />
            </span>
            <div className="space-y-1">
              <h3 className="font-display text-base font-semibold text-gold-900 dark:text-gold-50">
                Rules test not open yet
              </h3>
              <p className="text-sm text-[color:var(--color-text-muted)]">
                {AWAITING_RULES_MESSAGE}
              </p>
              <p className="pt-1 text-sm">
                <Link
                  to="/candidate/status"
                  className="inline-flex items-center gap-1 font-medium text-[color:var(--color-text)] underline-offset-2 hover:underline"
                >
                  <ArrowLeft size={14} aria-hidden="true" />
                  Back to my application
                </Link>
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 text-sm text-[color:var(--color-text-muted)]">
              <ShieldCheck
                size={16}
                className="mt-0.5 text-[color:var(--color-text-muted)]"
                aria-hidden="true"
              />
              <span>
                By clicking Begin you confirm you've read the rules above and
                will abide by them as a Babul Ilm teacher.
              </span>
            </div>
            <Button
              size="lg"
              icon={<Play size={18} aria-hidden="true" />}
              loading={startMutation.isPending}
              onClick={() => startMutation.mutate()}
            >
              Begin rules test
            </Button>
          </div>
        )}
      </section>
    </ListShell>
  );
}
